import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../../common/modal/Modal'
import { t } from '../../../i18n/i18n'
import { Button } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { RiskAnalysis, RiskAnalysisActionPlanResponse, RiskStatus, SimplifiedRisk } from '../../../api/schemas/schema'
import { GridItem } from '../../common/grid/GridItem'
import { Grid } from '../../common/grid/Grid'
import { api } from '../../../helpers/apiHelper'
import { DataContext } from '../../../context/DataContext'
import { handleMessage } from '../../../helpers/stateHelper'
import { RiskActionPlanInfo } from './RiskActionPlanInfo'
import { IItemState } from '../../../interfaces/IItemState'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'

type ModalRiskAnalyseEvaluateProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysis: RiskAnalysis
   open: boolean
   close: () => void
   selectedRisk: SimplifiedRisk
   disabled?: boolean
}

export const ModalRiskAnalyseEvaluate = ({ setRiskAnalysisState, analysis, open, close, selectedRisk, disabled }: ModalRiskAnalyseEvaluateProps) => {
   const {
      control,
      reset,
      handleSubmit,
      watch,
      setValue,
      formState: { isDirty, isSubmitting },
   } = useForm<SimplifiedRisk>({ disabled: disabled })

   const dataContext = useContext(DataContext)
   const setRootState = dataContext.setRootState
   const { userAccount } = dataContext.state

   useEffect(() => {
      if (selectedRisk) {
         reset(selectedRisk)
      }
   }, [selectedRisk, reset])

   useEffect(() => {
      if (watch('status') === RiskStatus.Acceptable) {
         setValue('proposedAction', null)
         setValue('estimatedCost', null)
      }
   })

   const onSubmit = async (data: SimplifiedRisk) => {
      try {
         data.modifiedBy = userAccount
         data.modifiedById = userAccount.id
         data.analysedBy = userAccount
         data.analysedById = userAccount.id

         const response: RiskAnalysisActionPlanResponse = (await api().createOrUpdateActionPlanForSimplifiedRisk(analysis.organisationId, analysis.id, data))
            .data

         const updatedRiskAnalysis = response.updatedAnalysis
         const updatedRisk = updatedRiskAnalysis.simplifiedRisks.find((r) => r.id === data.id)
         const createdActionPlan = response.actionPlan
         if (createdActionPlan == null) {
            updatedRiskAnalysis.actionPlans = updatedRiskAnalysis.actionPlans.filter((ap) => ap.riskId !== updatedRisk.id)
         }

         setRiskAnalysisState((prev) => ({
            ...prev,
            item: {
               ...prev.item,
               ...updatedRiskAnalysis,
            },
         }))

         close()
         reset(updatedRisk)
         handleMessage(setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('SimplifiedRiskAnalysis') }))
      } catch (error) {
         handleMessage(setRootState, 'error', t('CouldNotUpdateItem', { Item: t('SimplifiedRiskAnalysis') }))
      }
   }
   const onClose = () => {
      reset()
      close()
   }

   return (
      <DraggableModal title={t('AnalyseEvaluate')} open={open} onClose={onClose}>
         <Form onSubmit={handleSubmit(onSubmit)} >
            <Grid>
               <RiskActionPlanInfo control={control} watch={watch} risk={selectedRisk} />
               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={onClose} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>

                     {!disabled && (
                        <Button type="submit" appearance="primary" disabled={disabled || !isDirty || isSubmitting}>
                           {t('Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
